import CurrencyMap from 'country-currency-map';
import { Currency, PriceValue } from 'hydra-data-types';

/** A price with a currency. */
export class Price {
  public value: PriceValue;
  public currency: Currency;

  constructor(value: PriceValue, currency: Currency) {
    this.value = value;
    this.currency = currency;
  }

  /** The price value in it's main unit (e.g. `67.50` dollars instead of `6750` cents) */
  public valueOf(): number {
    return Price.inMainUnit(this.value);
  }

  /** The price and currency as a formatted string (e.g. `€120` or `$99.99`). */
  public toString(): string {
    return Price.formatWithCurrency(this.value, this.currency);
  }

  /** Convert a price value (eg: `6750` cents) to it's main unit (e.g `67.50`). */
  static inMainUnit(priceValue: PriceValue): number {
    return priceValue / 100;
  }

  /** Format a price value to a string containing the currency (e.g. `€120` or `AU$99.99`) */
  static formatWithCurrency(priceValue: PriceValue, currency: Currency): string {
    const price = Price.inMainUnit(priceValue);
    // A simple way to hide `00` cents
    const fixedPrice = price.toFixed(Number.isInteger(price) ? 0 : 2);
    return CurrencyMap.formatCurrency(fixedPrice, currency);
  }
}

export const CURRENCY_TO_LOCALE = {
  [Currency.USD]: 'en-US',
  [Currency.AUD]: 'en-AU',
  [Currency.GBP]: 'en-GB',
  [Currency.CAD]: 'en-CA',
  [Currency.NZD]: 'en-NZ',
  [Currency.SEK]: 'sv-SE',
  [Currency.NOK]: 'nb-NO',
  [Currency.EUR]: 'de-DE',
} as const;

/** Format a price value as a whole number with locale-appropriate thousand separators and currency symbol (e.g. `$25,000` or `250 000 kr`). */
export function formatWholePrice(priceValue: PriceValue, currency: Currency): string {
  return new Intl.NumberFormat(CURRENCY_TO_LOCALE[currency], {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(Price.inMainUnit(priceValue));
}

export const CURRENCY_TO_NAME = {
  [Currency.USD]: 'US Dollar',
  [Currency.AUD]: 'Australian Dollar',
  [Currency.GBP]: 'British Pound Sterling',
  [Currency.CAD]: 'Canadian Dollar',
  [Currency.NZD]: 'New Zealand Dollar',
  [Currency.SEK]: 'Swedish Krona',
  [Currency.NOK]: 'Norwegian Krone',
  [Currency.EUR]: 'Euro',
} as const;

// Selected test currencies
export const SUPPORTED_CURRENCIES: readonly Currency[] = Object.values(Currency);

/**  */
export function parseCurrency(currency: string): Currency {
  return Object.values<string>(Currency).includes(currency) ? (currency as Currency) : Currency.USD;
}
